import {
  string, array, func,
} from 'prop-types';
import React, {
  useEffect, memo, useState, useCallback,
} from 'react';
import classNames from 'classnames';
import noop from 'lodash/noop';
import { appendScript } from '../../../../../shared/utils/commonUtils';
import { getSessionData } from '../../offerUtils';

const ActiveAdvantagePostSell = ({
  widgetInitToken,
  widgetSubmitToken,
  locale,
  membershipEmail,
  firstOrderPaymentResult,
  trackingCallback,
  widgetNameSpace,
}) => {
  const [ showDarkLayer, setShowDarkLayer ] = useState(!!widgetInitToken);

  const destroyAAPostWidget = (renderer) => {
    renderer.then(({ destroy = noop }) => {
      destroy();
    });
  };

  const resolveOfferRenderer = resolve => () => {
    window.COMMERCEWidget && window.COMMERCEWidget.onReady((ACW) => {
      resolve(ACW);
    });
  };

  const findOfferRenderer = (widgetSDKUrl) => {
    return new Promise((resolve) => {
      const { offerRenderer } = window.COMMERCEWidget || {};
      if (offerRenderer) {
        resolve(offerRenderer);
      } else {
        widgetSDKUrl && appendScript(widgetSDKUrl, resolveOfferRenderer(resolve), false);
      }
    });
  };

  const getCardAndBillingInfo = () => {
    return firstOrderPaymentResult.reduce((res, item) => {
      res.billingInfo = res.billingInfo || item.billingContact;
      res.paymentAccounts = [ item.paymentAccount ].concat(res.paymentAccounts || {});
      return res;
    }, {});
  };

  const getMembershipInfo = () => {
    const { billingInfo = {} } = getCardAndBillingInfo();
    const { address = {} } = billingInfo;
    const { firstName = '', lastName = '', email = '' } = billingInfo;
    const {
      line1 = '', line2 = '', city = '', stateProvince = '', country = '', postalCode = '',
    } = address;
    return {
      BillingFullName: firstName + (firstName && lastName ? ' ' : '') + lastName,
      BillingAddressLine: line1 + (line1 && line2 ? ' ' : '') + line2,
      BillingCity: city + (city && stateProvince ? ', ' : '') + stateProvince,
      BillingState: country,
      BillingPostalCode: postalCode,
      BillingEmail: membershipEmail || email,
    };
  };

  const getCardInfo = () => {
    const { paymentAccounts = [] } = getCardAndBillingInfo();
    const matchedCard = paymentAccounts.filter(item => [ 'CREDIT_CARD', 'ECHECK', 'APPLEPAY', 'GOOGLEPAY' ].includes(item.tenderType)).map(item => item.maskedNumber)[0] || '';
    return {
      cardType: (matchedCard.match(/^{([\s\S]+?)}/) || [])[1],
      CardLastFour: matchedCard.trim().slice(-4) || '****',
    };
  };

  const handleAAPostInit = useCallback(() => {
    setShowDarkLayer(false);
  }, []);

  const mountAAPost = async () => {
    const { widgetAPIServerHost, widgetSDKUrl } = getSessionData(`${widgetNameSpace}-initOfferParams`);
    if (!widgetInitToken) return {};
    const offerRenderer = await findOfferRenderer(widgetSDKUrl);
    const { CardLastFour, cardType } = getCardInfo();
    const params = {
      jwt: widgetInitToken,
      locale,
      widgetAPIServerHost,
      membershipFor: getMembershipInfo(),
      CardLastFour,
      cardType,
      show: noop,
      hide: noop,
      initCallback: handleAAPostInit,
      submitData: { dynamicToken: widgetSubmitToken },
      trackingCallback,
    };
    return offerRenderer.renderActiveAdvantagePostSell(
      document.getElementById('offer-widget-AAPost'), params);
  };

  useEffect(() => {
    const renderer = mountAAPost();
    return () => {
      destroyAAPostWidget(renderer);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDarkLayer(false);
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className={classNames({
      'active-advantage-post-sell-offer': true,
      'dark-layer': showDarkLayer,
    })}
    id="offer-widget-AAPost" />
  );
};

ActiveAdvantagePostSell.propTypes = {
  widgetInitToken: string.isRequired,
  widgetSubmitToken: string.isRequired,
  widgetNameSpace: string.isRequired,
  locale: string,
  firstOrderPaymentResult: array,
  trackingCallback: func,
};

ActiveAdvantagePostSell.defaultProps = {
  locale: 'en_US',
  firstOrderPaymentResult: [],
  trackingCallback: noop,
};

export default memo(ActiveAdvantagePostSell);
